import _ from 'lodash';
import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { useWalletCategories } from '../hooks/useWalletCategories';

import { CreditCardSearchBar } from '../components/CreditCardSearchBar';
import { PopularCards } from '../components';
import { ExploreWallets } from '../components/ExploreWallets';
import {
  CardRewardsEarnRates,
  EarnRateInfo,
} from 'data/types/CardRewardsEarnRates';
import { useCategoryEarnRatesAndCreditsV2 } from '../data/shared/hooks/useBestEarnRatesV2';
import { useBestEarnRatesByCardV2 } from '../data/shared/hooks/useBestEarnRatesByCardV2';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import { EditPointValues } from '../components/EditPointValues';
import { useCreditCardState } from '../hooks/useCreditCardState';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { TableCellCategory } from '../components/design-system/TableCellCategory';
import { TableCellWithSubtext } from '../components/design-system/TableCellWithSubtext';
import { useCardModal } from '../hooks/useCardModal';
import { CardComboTemplates } from '../components/CardComboTemplates';
import { Modal } from '../components/design-system/Modal';
import { EditableTableCell } from '../components/design-system/EditableTableCell';
import { CategoryModal } from '../components/CategoryModal';
import { useParamStatePointValues } from '../hooks/useURLParamPointValues';
import { useMonthlySpendState } from '../data/shared/hooks/useMonthlySpendState';
import { useShareModal } from '../hooks/useShareModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { CardButton } from '../components/design-system/CardButton';
import { CardCarousel } from '../components/CardCarousel';
import { Stepper } from '../components/design-system/Stepper';
import { TableCardButton } from '../components/TableCardButton';
import { RemoveCardButton } from '../components/RemoveCardButton';
import { CopyButton } from '../components/CopyButton';
import { Tooltip } from '../components/Tooltip';
import { Navigation } from '../components/Navigation';
import { PageWrapper } from '../components/design-system/PageWrapper';
import { COLUMN_WIDTH } from '../constants';
import { ContentWrapper } from '../components/design-system/ContentWrapper';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { CreditCard } from '../data/shared/CreditCard';
import { CreditCardRewardsType } from '../data/shared/CreditCardRewardsTypes';
import { CategoryType } from 'data/shared/__autogenerated__/types/CreditCardCategoryTypes';
import { categoryEmojiMap } from 'data/shared/utils/CategoryEmojiMap';
import { useScreenSize } from '../hooks/useScreenSize';
import { getColumnWidths, getColumnStyle } from '../utils/tableColumnWidths';
import { MobileRewardsView } from '../components/MobileRewardsView';
import { EarnRateConditionsMessage } from '../components/EarnRateConditionsMessage';
import { BiltSpendRatioModal } from '../components/BiltSpendRatioModal';
import { SegmentedControl } from '../components/design-system/SegmentedControl';
import { ActionButton } from '../components/design-system/ActionButton';
import { SortableColumnHeader } from '../components/design-system/SortableColumnHeader';
import { useTableSort, SortDirection } from '../hooks/useTableSort';
import { useURLParamPromotionalTypes } from '../hooks/useURLParamPromotionalTypes';
import { PromotionalType } from 'data/shared/__autogenerated__/types/PromotionalType';
import { CategoryEarnRatePill } from '../components/CreditPills';
import {
  shouldShowCategory,
  isStandaloneCategory,
  isAlwaysShowCategory,
  getAllParentCategories,
  CategoryEarnRatesMap,
} from 'data/shared/utils/categoryFilterUtils';
import { findEarnRateForCategory } from '../utils/findMatchingEarnRate';

declare module 'lodash' {
  interface LoDashStatic {
    toPairsDeep<T>(obj: T): Array<[string, unknown]>;
  }
}

_.mixin({
  toPairsDeep: (obj: Record<string, unknown>): Array<[string, unknown]> =>
    _.flatMap(_.toPairs(obj), ([k, v]) =>
      _.isObjectLike(v) ? _.toPairsDeep(v) : [[k, v]]
    ),
});

// Helper function to map string categories to CategoryType
const mapStringToCategoryType = (category: string): CategoryType => {
  // Map string categories to CategoryType enum values
  return category as CategoryType;
};

// Enum for category display modes in cards view
enum CategoryDisplayMode {
  SHOW = 'show',
  COLLAPSE_INTO_ALL_PURCHASES = 'collapseIntoAllPurchases',
  COLLAPSE_INTO_PARENT = 'collapseIntoParent',
  HIDE = 'hide',
}

// Helper function to get parent category (e.g., "airlines" from "airlines.american")
const getParentCategory = (category: string): string | null => {
  const parts = category.split('.');
  if (parts.length < 2) {
    return null;
  }
  // Return the immediate parent (e.g., "airlines" from "airlines.american")
  return parts.slice(0, -1).join('.');
};

// Get display mode for a category in cards view
const getCategoryDisplayModeForCard = (
  category: string,
  card: CreditCard,
  categoryEarnRates: Record<
    CategoryType,
    {
      cardsWithCredits: EarnRateInfo[];
      sortedCardsByEarnRate: EarnRateInfo[];
    }
  >,
  bestEarnRatesForCard: EarnRateInfo[] // Categories actually in this card's list
): CategoryDisplayMode => {
  const categoryType = mapStringToCategoryType(category);
  const categoryData = categoryEarnRates[categoryType];
  const categoryBestCard = categoryData?.sortedCardsByEarnRate[0];

  // If category doesn't exist or card isn't best, hide it
  if (!categoryBestCard || categoryBestCard.card !== card) {
    return CategoryDisplayMode.HIDE;
  }

  // Always show "allPurchases" itself
  if (category === 'allPurchases' || category === 'all_purchases') {
    return CategoryDisplayMode.SHOW;
  }

  // Never collapse standalone categories (like rent) - they exist outside allPurchases hierarchy
  if (isStandaloneCategory(category)) {
    return CategoryDisplayMode.SHOW;
  }

  // Check if this category should collapse into allPurchases
  const allPurchasesData =
    categoryEarnRates['allPurchases'] ||
    (categoryEarnRates as any)['all_purchases'];
  const allPurchasesBestCard = allPurchasesData?.sortedCardsByEarnRate[0];

  if (
    allPurchasesBestCard &&
    allPurchasesBestCard.card === card &&
    allPurchasesBestCard.earnRate === categoryBestCard.earnRate &&
    allPurchasesBestCard.rewardsType === categoryBestCard.rewardsType
  ) {
    // Check if any other card beats this card for the category
    // If not, it's inherited from allPurchases
    const secondBestCard = categoryData?.sortedCardsByEarnRate[1];
    if (
      !secondBestCard ||
      secondBestCard.totalValue <= categoryBestCard.totalValue
    ) {
      return CategoryDisplayMode.COLLAPSE_INTO_ALL_PURCHASES;
    }
  }

  // Default: show normally
  return CategoryDisplayMode.SHOW;
};

// Process card categories for display in cards view
type ProcessedCategoryRow = {
  earnRateInfo: EarnRateInfo;
  displayMode: CategoryDisplayMode;
  collapsedCategories?: EarnRateInfo[]; // Categories collapsed into this one
  parentCategory?: string; // For parent group rows
};

const processCardCategoriesForDisplay = (
  card: CreditCard,
  bestEarnRatesForCard: EarnRateInfo[],
  categoryEarnRates: Record<
    CategoryType,
    {
      cardsWithCredits: EarnRateInfo[];
      sortedCardsByEarnRate: EarnRateInfo[];
    }
  >
): ProcessedCategoryRow[] => {
  const processed: ProcessedCategoryRow[] = [];
  const processedCategories = new Set<string>();

  // First pass: identify categories to collapse
  const categoriesToCollapseIntoAllPurchases: EarnRateInfo[] = [];

  for (const earnRateInfo of bestEarnRatesForCard) {
    const mode = getCategoryDisplayModeForCard(
      earnRateInfo.category,
      card,
      categoryEarnRates,
      bestEarnRatesForCard
    );

    if (mode === CategoryDisplayMode.COLLAPSE_INTO_ALL_PURCHASES) {
      categoriesToCollapseIntoAllPurchases.push(earnRateInfo);
      processedCategories.add(earnRateInfo.category);
    }
  }

  // Second pass: build processed rows
  for (const earnRateInfo of bestEarnRatesForCard) {
    if (processedCategories.has(earnRateInfo.category)) {
      continue; // Skip collapsed categories
    }

    const mode = getCategoryDisplayModeForCard(
      earnRateInfo.category,
      card,
      categoryEarnRates,
      bestEarnRatesForCard
    );

    if (mode === CategoryDisplayMode.HIDE) {
      continue;
    }

    // Check if this is allPurchases that should include collapsed categories
    if (
      (earnRateInfo.category === 'allPurchases' ||
        earnRateInfo.category === 'all_purchases') &&
      categoriesToCollapseIntoAllPurchases.length > 0
    ) {
      processed.push({
        earnRateInfo,
        displayMode: CategoryDisplayMode.SHOW,
        collapsedCategories: categoriesToCollapseIntoAllPurchases,
      });
      continue;
    }

    // Regular category
    processed.push({
      earnRateInfo,
      displayMode: mode,
    });
  }

  // Sort processed rows to maintain consistent order
  processed.sort((a, b) => {
    return a.earnRateInfo.key.localeCompare(b.earnRateInfo.key);
  });

  return processed;
};

export const RewardsEstimator = () => {
  const [selectedCards, setSelectedCards] = useCreditCardState();
  const {
    categoriesByIndex,
    handleAddCard,
    handleRemoveCard,
    handleSelectCategoriesForCard,
  } = useWalletCategories(selectedCards, setSelectedCards);
  const [isSearchBarSticky, setIsSearchBarSticky] = useState(false);
  const searchBarSentinelRef = useRef<HTMLDivElement>(null);

  // Get centralized promotional types state (hydrates from URL on mount, then writes to URL)
  const { promotionalTypes, setPromotionalTypes, resetPromotionalTypes } =
    useURLParamPromotionalTypes(selectedCards);

  const [cardModal, setShowCardModal] = useCardModal({
    handleRemoveCard,
    handleAddCard,
    selectedCards,
    onSelectCategoriesForCard: handleSelectCategoriesForCard,
    currentSelectedCategories: categoriesByIndex,
    promotionalTypes,
    setPromotionalTypes,
    resetPromotionalTypes,
  });
  const [shareModal] = useShareModal();

  // Detect when search bar becomes sticky
  useEffect(() => {
    const sentinel = searchBarSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSearchBarSticky(!entry.isIntersecting);
      },
      { threshold: [1] }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {shareModal}
      <Navigation />

      <ContentWrapper>
        <div>
          <h1
            style={{
              fontSize: 64,
              lineHeight: 1.1,
              // color: '#3F79A4'
            }}
          >
            There is no best credit card.
          </h1>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              // alignItems: 'center',
              gap: 10,
              paddingTop: 30,
            }}
          >
            <div style={{ minWidth: 300, fontSize: 18, paddingTop: 10 }}>
              <p>All rewards cards have strengths and weaknesses.</p>
              <p>
                To get the best rewards, we'll help build a wallet customized
                for you, then automate the rewards process.
              </p>
            </div>
            <div>
              <CardCarousel />
            </div>
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            // bottom: -200,
            left: 0,
            right: 0,
            height: 400,
            zIndex: 1,
            background: 'linear-gradient(#fff, #eef1f4)',
          }}
        ></div>
      </ContentWrapper>

      <PageWrapper>
        <div style={{}}>
          <Stepper step={1} />
          <h2>Choose your cards</h2>
          {/* <p style={{ fontSize: 18 }}>
            Add your cards to see how to get the best rewards.
          </p> */}
        </div>
        <div style={{ margin: '10px 0' }}>
          <CardComboTemplates
            setSelection={setSelectedCards}
            selectedCards={selectedCards}
          />
        </div>

        <div style={{ flexGrow: 1 }}>
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
              flexWrap: 'wrap',
            }}
          >
            <h3 style={{ margin: 20, marginLeft: 0 }}>
              This is
              <br /> your wallet:
            </h3>
            {selectedCards.map((card, index) => {
              return CreditCardDataFiltered[card] ? (
                <CardButton
                  key={`${card}-${index}`}
                  cardData={CreditCardDataFiltered[card]}
                  onClick={() => setShowCardModal({ card, walletIndex: index })}
                />
              ) : null;
            })}
          </div>
        </div>

        {/* Sentinel element to detect when search bar becomes sticky */}
        <div ref={searchBarSentinelRef} style={{ height: '1px' }} />

        {/* Sticky search bar */}
        <div
          style={{
            position: 'sticky',
            top: '60px',
            zIndex: 150,
            backgroundColor: isSearchBarSticky ? '#eef1f4' : 'transparent',
            padding: isSearchBarSticky ? '10px 0' : '0',
            marginLeft: isSearchBarSticky ? '-20px' : '0',
            marginRight: isSearchBarSticky ? '-20px' : '0',
            paddingLeft: isSearchBarSticky ? '20px' : '0',
            paddingRight: isSearchBarSticky ? '20px' : '0',
            transition: 'all 0.3s ease',
          }}
        >
          <div
            style={{
              maxWidth: 1200,
              transform: isSearchBarSticky ? 'scale(0.9)' : 'scale(1)',
              transformOrigin: 'left center',
              transition: 'transform 0.3s ease',
            }}
          >
            <CreditCardSearchBar
              setSelectedCards={setSelectedCards}
              handleAddCard={handleAddCard}
              handleRemoveCard={handleRemoveCard}
              selectedCards={selectedCards}
            />
          </div>
        </div>

        <div style={{ marginTop: 80 }}>
          <Stepper step={2} />
          <h2>View categories</h2>
          <p>
            Based on your wallet, here's how to get the best rewards for your
            spend.
          </p>
        </div>

        <div style={{ marginTop: 12 }}>
          <RewardsCardPreviews
            cards={selectedCards}
            handleRemoveCard={handleRemoveCard}
            handleAddCard={handleAddCard}
            setSelectedCards={setSelectedCards}
            promotionalTypes={promotionalTypes}
            setPromotionalTypes={setPromotionalTypes}
            resetPromotionalTypes={resetPromotionalTypes}
            categoriesByIndex={categoriesByIndex}
            onSelectCategoriesForCard={handleSelectCategoriesForCard}
          />
        </div>

        {/* <div style={{ marginTop: 140 }}>
          <Stepper step={3} />
          <h1>Pick additional cards</h1>
          <p>
            Based on a combination of our ratings, rewards strengths, and to
            complement your current wallet.
          </p>

          <CardRecommendations cards={selectedCards} />
        </div>
 */}
        <div style={{ marginTop: 140 }}>
          <Stepper step={3} />
          <h2>Save your wallet</h2>
          <p>
            Copy this link to save your wallet and to come back to it anytime,
            or share it with others
          </p>
          <CopyButton textToCopy={window.location.href} />
        </div>

        {cardModal}
      </PageWrapper>
    </>
  );
};

export const RewardsCardPreviews = ({
  cards,
  handleRemoveCard,
  handleAddCard,
  setSelectedCards,
  promotionalTypes,
  setPromotionalTypes,
  resetPromotionalTypes,
  categoriesByIndex: categoriesByIndexLocal,
  onSelectCategoriesForCard: handleSelectCategoriesForCardInPreview,
}: {
  cards: CreditCard[];
  handleRemoveCard: (card: CreditCard, walletIndex?: number) => void;
  handleAddCard: (card: CreditCard) => void;
  setSelectedCards: (cards: CreditCard[]) => void;
  promotionalTypes: PromotionalType[];
  setPromotionalTypes: (types: PromotionalType[]) => void;
  resetPromotionalTypes: () => void;
  categoriesByIndex: (string[][][] | undefined)[];
  onSelectCategoriesForCard: (
    card: CreditCard,
    selectedCategories: string[][][],
    walletIndex?: number
  ) => void;
}) => {
  // Get responsive column widths
  const screenSize = useScreenSize();
  const columnWidths = getColumnWidths(screenSize);

  // Show mobile view for screens smaller than 850px
  const isMobile = screenSize === 'mobile';

  const [isForeignPurchase, setIsForeignPurchase] = useState(false);
  const [monthlySpendOverrides, setMonthlySpendOverrides] = useState<
    Record<string, any>
  >({});
  const [monthlySpend, setMonthlySpend, resetMonthlySpendValues] =
    useMonthlySpendState(monthlySpendOverrides, setMonthlySpendOverrides);
  const { pointValues, setPointValues, resetPointValues } =
    useParamStatePointValues(cards, true);

  // Add view mode state
  const [viewMode, setViewMode] = useState<'categories' | 'cards'>('cards');

  // Sort state for table columns using custom hook
  type CategoryViewSortColumn = 'category' | 'card' | 'effectivePercent';
  type CardViewSortColumn = 'card' | 'effectivePercent';

  const {
    sortColumn: categorySortColumn,
    sortDirection: categorySortDirection,
    handleSort: handleCategorySort,
    resetSort: resetCategorySort,
  } = useTableSort<CategoryViewSortColumn>('desc');

  const {
    sortColumn: cardSortColumn,
    sortDirection: cardSortDirection,
    handleSort: handleCardSort,
    resetSort: resetCardSort,
  } = useTableSort<CardViewSortColumn>('desc');

  // Reset sort when switching views
  const handleViewModeChange = useCallback(
    (mode: 'categories' | 'cards') => {
      setViewMode(mode);
      resetCategorySort();
      resetCardSort();
    },
    [resetCategorySort, resetCardSort]
  );

  // Bilt spend ratio modal state
  const [showBiltSpendRatioModal, setShowBiltSpendRatioModal] = useState(false);

  // Get all possible categories from CategoryType enum
  const allCategories = useMemo(() => Object.values(CategoryType), []);

  // Map string categories to CategoryType for the hook
  const categoryTypes = useMemo(
    () => allCategories.map(mapStringToCategoryType),
    [allCategories]
  );

  // Use the new hook
  const categoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    cards,
    categoryTypes,
    pointValues,
    !isForeignPurchase, // isPurchasingInUSA: true when purchasing in USA, false when outside
    categoriesByIndexLocal,
    promotionalTypes
  );

  // Filter categories to only show relevant ones
  const categories = useMemo(() => {
    const filtered = allCategories.filter((category) =>
      shouldShowCategory(category, categoryEarnRates)
    );
    return filtered;
  }, [allCategories, categoryEarnRates]);

  // Sorted categories for category view
  const sortedCategories = useMemo(() => {
    if (!categorySortColumn) return categories;

    return [...categories].sort((a, b) => {
      const categoryTypeA = mapStringToCategoryType(a);
      const categoryTypeB = mapStringToCategoryType(b);
      const dataA = categoryEarnRates[categoryTypeA];
      const dataB = categoryEarnRates[categoryTypeB];
      const bestCardA = dataA?.sortedCardsByEarnRate[0];
      const bestCardB = dataB?.sortedCardsByEarnRate[0];

      let comparison = 0;
      switch (categorySortColumn) {
        case 'category':
          comparison = formatSpendCategory(a).localeCompare(
            formatSpendCategory(b)
          );
          break;
        case 'card':
          const cardNameA = bestCardA
            ? CreditCardDataFiltered[bestCardA.card]?.name || ''
            : '';
          const cardNameB = bestCardB
            ? CreditCardDataFiltered[bestCardB.card]?.name || ''
            : '';
          comparison = cardNameA.localeCompare(cardNameB);
          break;
        case 'effectivePercent':
          const effectiveA = bestCardA
            ? bestCardA.earnRate * (pointValues[bestCardA.rewardsType] || 1)
            : 0;
          const effectiveB = bestCardB
            ? bestCardB.earnRate * (pointValues[bestCardB.rewardsType] || 1)
            : 0;
          comparison = effectiveA - effectiveB;
          break;
      }
      return categorySortDirection === 'asc' ? comparison : -comparison;
    });
  }, [
    categories,
    categorySortColumn,
    categorySortDirection,
    categoryEarnRates,
    pointValues,
  ]);

  const [showEditPointValues, setShowEditPointValues] = useState(false);
  const [showCategoryModalCategory, setShowCategoryModalCategory] = useState<
    string | undefined
  >(undefined);
  const [customizePointAndSpendValues] = useState(true);

  const [cardModal, setShowCardModal] = useCardModal({
    handleRemoveCard,
    handleAddCard,
    selectedCards: cards,
    onSelectCategoriesForCard: handleSelectCategoriesForCardInPreview,
    currentSelectedCategories: categoriesByIndexLocal,
    promotionalTypes,
    setPromotionalTypes,
    resetPromotionalTypes,
  });

  // State for the card combo warning modal
  const [showComboWarningModal, setShowComboWarningModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CreditCard[]>([]);

  const headerRef = useRef<HTMLDivElement>(null);

  // Use the new hook for best earn rates by card
  const bestEarnRatesByCard = useBestEarnRatesByCardV2(
    cards,
    categories, // Use visible categories for display
    pointValues,
    categoriesByIndexLocal,
    !isForeignPurchase, // isPurchasingInUSA: true when purchasing in USA, false when outside
    promotionalTypes
  );

  // Sort cards by total value (sum of totalValue across categories where the card is best)
  // or by user-selected sort column
  const sortedCardIndices = useMemo(() => {
    // Calculate max effective percent and total value for each card position
    const cardValues = cards.map((card, cardIndex) => {
      const earnRates = bestEarnRatesByCard[cardIndex] || [];
      const totalValue = earnRates.reduce(
        (sum, rate) => sum + rate.totalValue,
        0
      );
      // Calculate max effective percent for this card
      const maxEffectivePercent = earnRates.reduce((max, rate) => {
        const effectivePercent =
          rate.earnRate * (pointValues[rate.rewardsType] || 1);
        return Math.max(max, effectivePercent);
      }, 0);
      const cardName = CreditCardDataFiltered[card]?.name || '';
      return { cardIndex, totalValue, maxEffectivePercent, cardName };
    });

    // Apply user sort if selected, otherwise default to total value
    return cardValues
      .sort((a, b) => {
        if (cardSortColumn) {
          let comparison = 0;
          switch (cardSortColumn) {
            case 'card':
              comparison = a.cardName.localeCompare(b.cardName);
              break;
            case 'effectivePercent':
              comparison = a.maxEffectivePercent - b.maxEffectivePercent;
              break;
          }
          return cardSortDirection === 'asc' ? comparison : -comparison;
        }
        // Default: sort by total value (highest first)
        if (b.totalValue !== a.totalValue) {
          return b.totalValue - a.totalValue;
        }
        return a.cardIndex - b.cardIndex;
      })
      .map((item) => item.cardIndex);
  }, [
    cards,
    bestEarnRatesByCard,
    cardSortColumn,
    cardSortDirection,
    pointValues,
  ]);

  const totalRewards = useMemo(() => {
    return (
      _.sum(
        categories.map((category) => {
          const categoryType = mapStringToCategoryType(category);
          const categoryData =
            categoryEarnRates[categoryType as keyof typeof categoryEarnRates];
          if (categoryData && categoryData.sortedCardsByEarnRate.length > 0) {
            const bestCard = categoryData.sortedCardsByEarnRate[0];
            // Use the pre-calculated earn rate from EarnRateInfo
            const earnRate = bestCard.earnRate;
            const spend = _.get(monthlySpend, category) || 0;
            const pointValue = pointValues[bestCard.rewardsType] || 1;

            return Math.round(earnRate * pointValue * spend);
          }
          return 0;
        })
      ) / 100
    );
  }, [categories, categoryEarnRates, monthlySpend, pointValues]);

  const cardCountToCarry = useMemo(() => {
    // Count wallet positions that have at least one category assigned
    return bestEarnRatesByCard.filter((rates) => rates.length > 0).length;
  }, [bestEarnRatesByCard]);
  const categoryCount = useMemo(
    () => bestEarnRatesByCard.flat().length,
    [bestEarnRatesByCard]
  );

  const handlePointValuesChange = useCallback(
    (rewardsType: CreditCardRewardsType, value: string) => {
      setPointValues(rewardsType, value);
    },
    [setPointValues]
  );

  const handleMonthlySpendChange = useCallback(
    (key: string, value: string) => {
      setMonthlySpend((prevMonthlySpend: CardRewardsEarnRates) => ({
        ..._.set(prevMonthlySpend, key, Number(value)),
      }));
    },
    [setMonthlySpend]
  );

  return (
    <>
      {cardModal}
      {showCategoryModalCategory && (
        <CategoryModal
          cards={cards}
          category={showCategoryModalCategory}
          monthlySpend={monthlySpend}
          pointValues={pointValues}
          selectedCategoriesByCard={categoriesByIndexLocal}
          handleClose={() => setShowCategoryModalCategory(undefined)}
          onOpenCardModal={(card) => {
            setShowCategoryModalCategory(undefined);
            setShowCardModal(card);
          }}
          promotionalTypes={promotionalTypes}
        />
      )}
      {/* {showEditMonthlySpend && (
        <EditMonthlySpend
          monthlySpend={monthlySpend}
          setMonthlySpend={setMonthlySpend}
          resetMonthlySpend={resetMonthlySpendValues}
          handleClose={() => setShowEditMonthlySpend(false)}
        />
      )} */}
      {showEditPointValues && (
        <EditPointValues
          cards={cards}
          pointValues={pointValues}
          setPointValues={setPointValues}
          resetPointValues={resetPointValues}
          handleClose={() => setShowEditPointValues(false)}
          promotionalTypes={promotionalTypes}
          setPromotionalTypes={setPromotionalTypes}
          resetPromotionalTypes={resetPromotionalTypes}
        />
      )}

      {/* Bilt Spend Ratio Modal */}
      {showBiltSpendRatioModal && (
        <BiltSpendRatioModal
          handleClose={() => setShowBiltSpendRatioModal(false)}
          promotionalTypes={promotionalTypes}
          setPromotionalTypes={setPromotionalTypes}
        />
      )}

      {/* Card Combo Warning Modal */}
      <Modal
        isOpen={showComboWarningModal}
        handleClose={() => {
          setShowComboWarningModal(false);
          setSelectedTemplate([]);
        }}
        cancelText="Cancel"
        extraFooterButtons={
          <ActionButton
            onClick={() => {
              setSelectedCards(selectedTemplate);
              setShowComboWarningModal(false);
              setSelectedTemplate([]);
            }}
          >
            Ok
          </ActionButton>
        }
      >
        <h2>Replace current cards?</h2>
        <p>
          You already have cards selected. Selecting a new card combo will
          replace the current cards.
        </p>
      </Modal>

      {!cards.length && (
        <h3 style={{ textAlign: 'center', margin: '0 0 20px 0' }}>
          Select a card to start:
        </h3>
      )}
      {cards.length === 1 && (
        <h4 style={{ textAlign: 'center', margin: '0 0 20px 0' }}>
          Tip: Add another card to see your wallet's best spend categories
        </h4>
      )}

      {cards.length > 0 && isMobile && (
        <MobileRewardsView
          cards={cards}
          bestEarnRatesByCard={bestEarnRatesByCard}
          handleRemoveCard={handleRemoveCard}
          setShowCardModal={setShowCardModal}
          customizePointAndSpendValues={customizePointAndSpendValues}
          pointValues={pointValues}
          inUSA={!isForeignPurchase}
          setInUSA={(inUSA) => setIsForeignPurchase(!inUSA)}
          setShowEditPointValues={setShowEditPointValues}
        />
      )}

      {isMobile && (
        <>
          <PopularCards
            onAddCard={handleAddCard}
            onRemoveCard={handleRemoveCard}
            selectedCards={cards}
            showTabs={true}
            isMobile={true}
            promotionalTypes={promotionalTypes}
            selectedCategoriesByCard={categoriesByIndexLocal}
            isPurchasingInUSA={!isForeignPurchase}
          />
        </>
      )}

      {isMobile && (
        <ExploreWallets
          hasCardsSelected={cards.length > 0}
          isMobile={isMobile}
        />
      )}

      <>
        {/* Desktop/Tablet View */}
        {!isMobile && (
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div
              style={{
                width: 'fit-content',
                margin: '0 auto',
                maxWidth: '100%',
              }}
            >
              <div
                style={{
                  minWidth: COLUMN_WIDTH,
                }}
              >
                {cards.length > 0 && (
                  <>
                    {/* Header */}
                    <div
                      ref={headerRef}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minWidth: '300px',
                        marginBottom: '20px',
                        paddingLeft: 24,
                      }}
                    >
                      {/* Segmented Control - Desktop only, left side */}
                      {!isMobile && (
                        <SegmentedControl
                          title="Explore by"
                          options={[
                            { value: 'cards', label: 'Cards', icon: '💳' },
                            {
                              value: 'categories',
                              label: 'Categories',
                              icon: '📊',
                            },
                          ]}
                          value={viewMode}
                          onChange={(value) =>
                            handleViewModeChange(
                              value as 'cards' | 'categories'
                            )
                          }
                        />
                      )}

                      {/* Action buttons, right side */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '24px',
                          // backgroundColor: '#fff',
                          borderRadius: '20px',
                          overflow: 'visible',
                          padding: '10px 24px 10px 24px',
                        }}
                      >
                        {/* <button
                          onClick={() => {
                            resetPointValues();
                            resetMonthlySpendValues();
                            // setCustomizePointAndSpendValues(false);
                          }}
                          style={{
                            color: '#4DA9E6',
                            fontWeight: 400,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faRefresh}
                            style={{ marginRight: 4 }}
                          />
                          Reset all
                        </button> */}

                        <ActionButton
                          onClick={() => setShowEditPointValues(true)}
                        >
                          Advanced settings
                        </ActionButton>
                        <Tooltip
                          content={
                            isForeignPurchase
                              ? 'Applies foreign transaction fees and filters out U.S.-only categories'
                              : 'Not applying foreign transaction fees. U.S.-only categories are shown'
                          }
                          position="top"
                          maxWidth={600}
                        >
                          <div>
                            <SegmentedControl
                              title="Location:"
                              options={[
                                { value: 'us', label: 'U.S.', icon: '🇺🇸' },
                                {
                                  value: 'foreign',
                                  label: 'Other',
                                  icon: '🌍',
                                },
                              ]}
                              value={isForeignPurchase ? 'foreign' : 'us'}
                              onChange={(value) =>
                                setIsForeignPurchase(value === 'foreign')
                              }
                            />
                          </div>
                        </Tooltip>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        fontFamily: 'Merriweather',
                        color: '#000',
                        padding: '0px 24px 10px 24px',
                      }}
                    >
                      {viewMode === 'categories' ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.category),
                              fontWeight: 400,
                            }}
                          >
                            <SortableColumnHeader
                              label="Category"
                              columnKey="category"
                              currentColumn={categorySortColumn}
                              direction={categorySortDirection}
                              onSort={handleCategorySort}
                            />
                          </div>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.card),
                              fontWeight: 400,
                            }}
                          >
                            <SortableColumnHeader
                              label="Recommended card"
                              columnKey="card"
                              currentColumn={categorySortColumn}
                              direction={categorySortDirection}
                              onSort={handleCategorySort}
                            />
                          </div>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.annualFee),
                              fontWeight: 400,
                            }}
                          >
                            Annual fee
                          </div>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.earnRate),
                              fontWeight: 400,
                              paddingLeft: 20,
                            }}
                          >
                            Earn rate
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.card),
                              fontWeight: 400,
                            }}
                          >
                            <SortableColumnHeader
                              label={`Cards (${cardCountToCarry}/${cards.length})`}
                              columnKey="card"
                              currentColumn={cardSortColumn}
                              direction={cardSortDirection}
                              onSort={handleCardSort}
                            />
                          </div>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.annualFee),
                              fontWeight: 400,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <div>Annual fee</div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: '#5D6C70',
                                  fontWeight: 'normal',
                                }}
                              >
                                Total: $
                                {_.sum(
                                  cards.map(
                                    (card) =>
                                      CreditCardDataFiltered[card]?.fees
                                        ?.annual_fee_amount_dollars ?? 0
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.earnRate),
                              fontWeight: 400,
                              textAlign: 'center',
                            }}
                          >
                            Earn rate
                          </div>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.category),
                              fontWeight: 400,
                              paddingLeft: 10,
                            }}
                          >
                            Category
                          </div>
                        </div>
                      )}
                      {customizePointAndSpendValues && (
                        <>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.centsPerPoint),
                              fontWeight: 400,
                              textAlign: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '4px 8px',
                              margin: '-4px -8px',
                            }}
                          >
                            <span
                              style={{
                                fontFamily: 'Merriweather',
                                fontWeight: 400,
                                color: '#000',
                              }}
                            >
                              Cents per point
                            </span>
                          </div>
                          <div
                            style={{
                              ...getColumnStyle(columnWidths.centsPerDollar),
                              fontWeight: 400,
                              textAlign: 'center',
                            }}
                          >
                            <SortableColumnHeader
                              label="Effective percent back"
                              columnKey="effectivePercent"
                              currentColumn={
                                viewMode === 'categories'
                                  ? categorySortColumn
                                  : cardSortColumn
                              }
                              direction={
                                viewMode === 'categories'
                                  ? categorySortDirection
                                  : cardSortDirection
                              }
                              onSort={
                                viewMode === 'categories'
                                  ? handleCategorySort
                                  : handleCardSort
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Table Body */}
                    <div>
                      {viewMode === 'categories'
                        ? // Categories view - group by categories
                          sortedCategories.map((category) => {
                            const categoryType =
                              mapStringToCategoryType(category);
                            const categoryData =
                              categoryEarnRates[categoryType];
                            const bestCard =
                              categoryData?.sortedCardsByEarnRate[0];

                            if (!bestCard) {
                              return null;
                            }

                            const { rewardsType, earnRate } = bestCard;
                            const spend = _.get(monthlySpend, category) || 0;

                            return (
                              <div
                                key={`category-${category}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  backgroundColor: '#fff',
                                  marginBottom: '10px',
                                  borderRadius: '20px',
                                  padding: '10px 24px',
                                }}
                              >
                                <div
                                  style={getColumnStyle(columnWidths.category)}
                                >
                                  <button
                                    onClick={() =>
                                      setShowCategoryModalCategory(category)
                                    }
                                    style={{
                                      backgroundColor: 'transparent',
                                      borderRadius: '8px',
                                      padding: '8px 14px',
                                      border: 'none',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <TableCellCategory
                                      text={categoryEmojiMap[category]}
                                      subText={formatSpendCategory(category)}
                                    />
                                  </button>
                                </div>
                                <div
                                  style={{
                                    ...getColumnStyle(columnWidths.card),
                                    position: 'relative',
                                  }}
                                >
                                  <RemoveCardButton
                                    onRemove={() =>
                                      handleRemoveCard(bestCard.card)
                                    }
                                  >
                                    {CreditCardDataFiltered[bestCard.card] && (
                                      <TableCardButton
                                        cardData={
                                          CreditCardDataFiltered[bestCard.card]
                                        }
                                        onClick={() =>
                                          setShowCardModal(bestCard.card)
                                        }
                                      />
                                    )}
                                  </RemoveCardButton>
                                </div>
                                <div
                                  style={{
                                    ...getColumnStyle(columnWidths.annualFee),
                                    textAlign: 'center',
                                  }}
                                >
                                  $
                                  {CreditCardDataFiltered[bestCard.card]?.fees
                                    ?.annual_fee_amount_dollars ?? 0}
                                </div>
                                <div
                                  style={getColumnStyle(columnWidths.earnRate)}
                                >
                                  <button
                                    style={{ width: 110 }}
                                    onClick={() =>
                                      setShowCategoryModalCategory(category)
                                    }
                                  >
                                    <TableCellWithSubtext
                                      text={formatEarnRate(
                                        earnRate,
                                        rewardsType
                                      )}
                                      subText={formatRewardsType(rewardsType)}
                                    />
                                  </button>
                                </div>
                                {customizePointAndSpendValues && (
                                  <>
                                    <div
                                      style={{
                                        ...getColumnStyle(
                                          columnWidths.centsPerPoint
                                        ),
                                        textAlign: 'center',
                                      }}
                                    >
                                      {rewardsType === 'cash_back' ? (
                                        1
                                      ) : (
                                        <EditableTableCell
                                          style={{ width: 120 }}
                                          suffix=" cpp"
                                          value={String(
                                            pointValues[rewardsType]
                                          )}
                                          onChange={(value) =>
                                            setPointValues(rewardsType, value)
                                          }
                                        />
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        ...getColumnStyle(
                                          columnWidths.centsPerDollar
                                        ),
                                        textAlign: 'center',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Math.round(
                                        earnRate *
                                          pointValues[rewardsType] *
                                          100
                                      ) / 100}
                                      %
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })
                        : // Cards view - group by cards (sorted by total value)
                          sortedCardIndices.map((cardIndex) => {
                            const card = cards[cardIndex];
                            const bestEarnRatesForCard =
                              bestEarnRatesByCard[cardIndex] || [];

                            if (bestEarnRatesForCard.length === 0) {
                              return null;
                            }

                            // Process categories for display (with collapse logic)
                            const processedRows =
                              processCardCategoriesForDisplay(
                                card,
                                bestEarnRatesForCard,
                                categoryEarnRates
                              );

                            return (
                              <div
                                key={`card-container-${card}-${cardIndex}`}
                                style={{
                                  backgroundColor: '#fff',
                                  marginBottom: '10px',
                                  borderRadius: '20px',
                                  overflow: 'hidden',
                                }}
                              >
                                {processedRows.map((processedRow, index) => {
                                  const { rewardsType, earnRate, category } =
                                    processedRow.earnRateInfo;
                                  const isLastRowInGroup =
                                    index === processedRows.length - 1;
                                  const isFirstRowInGroup = index === 0;
                                  const {
                                    collapsedCategories,
                                    parentCategory,
                                  } = processedRow;

                                  // Determine category display text
                                  let categoryDisplayText =
                                    formatSpendCategory(category);

                                  return (
                                    <div
                                      key={`${processedRow.earnRateInfo.key}-${card}`}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding:
                                          isFirstRowInGroup && isLastRowInGroup
                                            ? '20px 20px'
                                            : isFirstRowInGroup
                                            ? '20px 20px 0px 20px'
                                            : isLastRowInGroup
                                            ? '0px 20px 20px 20px'
                                            : '0px 20px',
                                      }}
                                    >
                                      <div
                                        style={{
                                          ...getColumnStyle(columnWidths.card),
                                          position: 'relative',
                                        }}
                                      >
                                        {index === 0 && (
                                          <RemoveCardButton
                                            onRemove={() =>
                                              handleRemoveCard(card, cardIndex)
                                            }
                                          >
                                            {CreditCardDataFiltered[card] && (
                                              <TableCardButton
                                                cardData={
                                                  CreditCardDataFiltered[card]
                                                }
                                                onClick={() =>
                                                  setShowCardModal({
                                                    card,
                                                    walletIndex: cardIndex,
                                                  })
                                                }
                                              />
                                            )}
                                          </RemoveCardButton>
                                        )}
                                      </div>
                                      <div
                                        style={{
                                          ...getColumnStyle(
                                            columnWidths.annualFee
                                          ),
                                          textAlign: 'center',
                                        }}
                                      >
                                        {index === 0 && (
                                          <>
                                            $
                                            {CreditCardDataFiltered[card]?.fees
                                              ?.annual_fee_amount_dollars ?? 0}
                                          </>
                                        )}
                                      </div>
                                      <div
                                        style={getColumnStyle(
                                          columnWidths.earnRate
                                        )}
                                      >
                                        <button
                                          style={{ width: 110 }}
                                          onClick={() =>
                                            setShowCategoryModalCategory(
                                              category
                                            )
                                          }
                                        >
                                          <TableCellWithSubtext
                                            text={formatEarnRate(
                                              earnRate,
                                              rewardsType
                                            )}
                                            subText={formatRewardsType(
                                              rewardsType
                                            )}
                                          />
                                        </button>
                                      </div>
                                      <div
                                        style={getColumnStyle(
                                          columnWidths.category
                                        )}
                                      >
                                        <button
                                          onClick={() =>
                                            setShowCategoryModalCategory(
                                              category
                                            )
                                          }
                                          style={{
                                            backgroundColor: '#EEF1F4',
                                            borderRadius: '14px',
                                            padding: '10px 16px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s',
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              '#E0E5E8';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              '#EEF1F4';
                                          }}
                                        >
                                          <TableCellCategory
                                            text={categoryEmojiMap[category]}
                                            subText={categoryDisplayText}
                                          />
                                        </button>
                                        {/* Collapsed categories as pills (only for allPurchases) */}
                                        {collapsedCategories &&
                                          collapsedCategories.length > 0 && (
                                            <div
                                              style={{
                                                paddingLeft: '30px',
                                                marginTop: '8px',
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: '12px',
                                                  color: '#666',
                                                  marginBottom: '4px',
                                                }}
                                              >
                                                Includes:
                                              </div>
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  flexWrap: 'wrap',
                                                  gap: '6px',
                                                  alignItems: 'center',
                                                }}
                                              >
                                                {collapsedCategories.map(
                                                  (collapsed) => (
                                                    <CategoryEarnRatePill
                                                      key={`pill-${collapsed.category}-${collapsed.key}`}
                                                      category={
                                                        collapsed.category
                                                      }
                                                      earnRate={
                                                        collapsed.earnRate
                                                      }
                                                      rewardsType={
                                                        collapsed.rewardsType
                                                      }
                                                      hideEarnRate={true}
                                                    />
                                                  )
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        {/* Add conditions text under category if restrictions apply */}
                                        <div
                                          style={{
                                            margin: '4px 4px 4px 0',
                                          }}
                                        >
                                          {(() => {
                                            const cardCats =
                                              categoriesByIndexLocal[cardIndex];
                                            const matched =
                                              findEarnRateForCategory(
                                                card,
                                                category,
                                                cardCats
                                              );
                                            return matched ? (
                                              <EarnRateConditionsMessage
                                                rate={matched.rate}
                                                showSharedCategories={true}
                                                userSelectedCategories={
                                                  matched.userSelections
                                                }
                                                currentCategory={category}
                                                promotionalType={
                                                  processedRow.earnRateInfo
                                                    .promotionalType
                                                }
                                                onEditBiltSpendRatio={() =>
                                                  setShowBiltSpendRatioModal(
                                                    true
                                                  )
                                                }
                                              />
                                            ) : null;
                                          })()}
                                        </div>
                                      </div>
                                      {customizePointAndSpendValues && (
                                        <>
                                          <div
                                            style={{
                                              ...getColumnStyle(
                                                columnWidths.centsPerPoint
                                              ),
                                              textAlign: 'center',
                                            }}
                                          >
                                            {rewardsType === 'cash_back' ? (
                                              1
                                            ) : (
                                              <EditableTableCell
                                                style={{ width: 120 }}
                                                suffix=" cpp"
                                                value={String(
                                                  pointValues[rewardsType]
                                                )}
                                                onChange={(value) =>
                                                  setPointValues(
                                                    rewardsType,
                                                    value
                                                  )
                                                }
                                              />
                                            )}
                                          </div>
                                          <div
                                            style={{
                                              ...getColumnStyle(
                                                columnWidths.centsPerDollar
                                              ),
                                              textAlign: 'center',
                                              fontWeight: 600,
                                            }}
                                          >
                                            {Math.round(
                                              earnRate *
                                                pointValues[rewardsType] *
                                                100
                                            ) / 100}
                                            %
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}

                      {/* Unused Cards Section */}
                      {(() => {
                        // Find cards that are not used in any category
                        const unusedCardsWithIndex = cards
                          .map((card, idx) => ({ card, walletIndex: idx }))
                          .filter(
                            ({ walletIndex }) =>
                              !(bestEarnRatesByCard[walletIndex]?.length > 0)
                          );
                        const unusedCards = unusedCardsWithIndex.map(
                          (c) => c.card
                        );

                        if (unusedCards.length === 0) {
                          return null;
                        }

                        return (
                          <>
                            {unusedCardsWithIndex.map(
                              (
                                { card, walletIndex: unusedWalletIndex },
                                unusedIndex
                              ) => (
                                <div
                                  key={`unused-${card}-${unusedIndex}`}
                                  style={{
                                    backgroundColor: '#fff',
                                    marginBottom: '10px',
                                    padding: '10px 24px',
                                    borderRadius: '20px',
                                    opacity: 0.5,
                                  }}
                                  onMouseEnter={(e) => {
                                    const removeButton =
                                      e.currentTarget.querySelector(
                                        '.remove-card-button'
                                      ) as HTMLElement;
                                    if (removeButton) {
                                      removeButton.style.opacity = '1';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    const removeButton =
                                      e.currentTarget.querySelector(
                                        '.remove-card-button'
                                      ) as HTMLElement;
                                    if (removeButton) {
                                      removeButton.style.opacity = '0';
                                    }
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {viewMode === 'categories' ? (
                                      <>
                                        <div
                                          style={getColumnStyle(
                                            columnWidths.category
                                          )}
                                        >
                                          <span
                                            style={{
                                              color: '#999',
                                              fontStyle: 'italic',
                                            }}
                                          >
                                            -
                                          </span>
                                        </div>
                                        <div
                                          style={{
                                            ...getColumnStyle(
                                              columnWidths.card
                                            ),
                                            position: 'relative',
                                          }}
                                        >
                                          <RemoveCardButton
                                            onRemove={() =>
                                              handleRemoveCard(
                                                card,
                                                unusedWalletIndex
                                              )
                                            }
                                          >
                                            {CreditCardDataFiltered[card] && (
                                              <TableCardButton
                                                style={{
                                                  opacity: '0.5',
                                                }}
                                                cardData={
                                                  CreditCardDataFiltered[card]
                                                }
                                                onClick={() =>
                                                  setShowCardModal({
                                                    card,
                                                    walletIndex:
                                                      unusedWalletIndex,
                                                  })
                                                }
                                              />
                                            )}
                                          </RemoveCardButton>
                                        </div>
                                        <div
                                          style={{
                                            ...getColumnStyle(
                                              columnWidths.annualFee
                                            ),
                                            textAlign: 'center',
                                          }}
                                        >
                                          $
                                          {CreditCardDataFiltered[card]?.fees
                                            ?.annual_fee_amount_dollars ?? 0}
                                        </div>
                                        <div
                                          style={getColumnStyle(
                                            columnWidths.earnRate
                                          )}
                                        >
                                          <span
                                            style={{
                                              color: '#999',
                                              fontStyle: 'italic',
                                            }}
                                          >
                                            -
                                          </span>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div
                                          style={{
                                            ...getColumnStyle(
                                              columnWidths.card
                                            ),
                                            position: 'relative',
                                          }}
                                        >
                                          <RemoveCardButton
                                            onRemove={() =>
                                              handleRemoveCard(
                                                card,
                                                unusedWalletIndex
                                              )
                                            }
                                          >
                                            {CreditCardDataFiltered[card] && (
                                              <TableCardButton
                                                style={{
                                                  opacity: '0.5',
                                                }}
                                                cardData={
                                                  CreditCardDataFiltered[card]
                                                }
                                                onClick={() =>
                                                  setShowCardModal({
                                                    card,
                                                    walletIndex:
                                                      unusedWalletIndex,
                                                  })
                                                }
                                              />
                                            )}
                                          </RemoveCardButton>
                                        </div>
                                        <div
                                          style={{
                                            ...getColumnStyle(
                                              columnWidths.annualFee
                                            ),
                                            textAlign: 'center',
                                          }}
                                        >
                                          $
                                          {CreditCardDataFiltered[card]?.fees
                                            ?.annual_fee_amount_dollars ?? 0}
                                        </div>
                                        <div
                                          style={getColumnStyle(
                                            columnWidths.earnRate
                                          )}
                                        >
                                          <span
                                            style={{
                                              color: '#999',
                                              fontStyle: 'italic',
                                            }}
                                          >
                                            -
                                          </span>
                                        </div>
                                        <div
                                          style={getColumnStyle(
                                            columnWidths.category
                                          )}
                                        >
                                          <span
                                            style={{
                                              color: '#999',
                                              fontStyle: 'italic',
                                            }}
                                          >
                                            -
                                          </span>
                                        </div>
                                      </>
                                    )}
                                    {customizePointAndSpendValues && (
                                      <>
                                        <div
                                          style={{
                                            ...getColumnStyle(
                                              columnWidths.centsPerPoint
                                            ),
                                            textAlign: 'center',
                                          }}
                                        >
                                          {CreditCardDataFiltered[card]
                                            ?.rewardsType === 'cash_back' ? (
                                            1
                                          ) : (
                                            <EditableTableCell
                                              style={{ width: 120 }}
                                              suffix=" cpp"
                                              value={String(
                                                pointValues[
                                                  CreditCardDataFiltered[card]
                                                    ?.rewardsType || ''
                                                ] || 1
                                              )}
                                              onChange={(value) =>
                                                setPointValues(
                                                  CreditCardDataFiltered[card]
                                                    ?.rewardsType || '',
                                                  value
                                                )
                                              }
                                            />
                                          )}
                                        </div>
                                        <div
                                          style={{
                                            ...getColumnStyle(
                                              columnWidths.centsPerDollar
                                            ),
                                            textAlign: 'center',
                                          }}
                                        >
                                          -
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </>
                )}

                {/* PopularCards moved below table - always shown even when no cards are selected */}
                <div>
                  {cards.length > 0 && (
                    <div style={{ marginTop: 40, padding: '0 20px' }}>
                      <h2>Find your next card</h2>
                      <p>Click any card to see how it works with your wallet</p>
                    </div>
                  )}
                  <PopularCards
                    onAddCard={handleAddCard}
                    onRemoveCard={handleRemoveCard}
                    selectedCards={cards}
                    showTabs={true}
                    promotionalTypes={promotionalTypes}
                    selectedCategoriesByCard={categoriesByIndexLocal}
                    isPurchasingInUSA={!isForeignPurchase}
                  />

                  {/* Explore other wallets section */}
                  <ExploreWallets
                    hasCardsSelected={cards.length > 0}
                    isMobile={isMobile}
                  />
                  {/* 
                  <div style={{ marginTop: 48 }}>
                    <div
                      style={{
                        padding: isMobile ? '0' : '0 24px',
                        marginBottom: 20,
                      }}
                    >
                      <h2>Why we started CardClues</h2>
                      <p>
                        I've had a lot of trouble keeping track of which card to
                        use where, and what cards to get next to find the ideal
                        "balanced" wallet for my needs. CardClues is partially a
                        tool that I built for myself, but also something I'd
                        like to share with the community.
                      </p>
                      <p>
                        Traditional credit card review websites give a lot of
                        information, but it's difficult to see how the cards
                        work together to help you achieve your goals.
                      </p>
                      <p>CardClues is a tool that</p>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    </>
  );
};
